import { useState } from 'react';
import { Card, CardBody, CardHeader, Icon } from '@repo/ui';
import { DataTable, DataTableColumn } from '@xtnd/web/shared/components';

import { Writeoff } from '../../types';
import { dateUtil, numberUtil } from '@xtnd/web/shared';

interface WriteOffTableProps {
  data: Writeoff[];
  isLoading: boolean;
  error: Error | null;
}

export const WriteoffsTable = ({ data, isLoading, error }: WriteOffTableProps) => {
  const [isExpand, setIsExpand] = useState<boolean>(true);
  const columns: DataTableColumn<Writeoff>[] = [
    {
      key: 'date',
      header: 'Date',
      align: 'left',
      renderCell: writeoff => {
        return (
          <span className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
            {dateUtil.formatDate(new Date(writeoff.date), dateUtil.DateFormat.US)}
          </span>
        );
      },
    },
    {
      key: 'total',
      header: 'Total',
      align: 'right',
      renderCell: writeoff => (
        <span className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
          {numberUtil.formatCurrency(writeoff.total)}
        </span>
      ),
    },
    {
      key: 'fees',
      header: 'Fees',
      align: 'right',
      renderCell: writeoff => (
        <span className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
          {numberUtil.formatCurrency(writeoff.fees || 0)}
        </span>
      ),
    },
    {
      key: 'expenses',
      header: 'Expenses',
      align: 'right',
      renderCell: writeoff => (
        <span className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
          {numberUtil.formatCurrency(writeoff.expenses || 0)}
        </span>
      ),
    },
    {
      key: 'text',
      header: 'Text',
      align: 'left',
      renderCell: writeoff => (
        <span className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
          {writeoff.text}
        </span>
      ),
    },
  ];

  const renderHeader = () => (
    <button
      className="flex items-center justify-between w-full"
      onClick={() => setIsExpand(prev => !prev)}
      aria-label={isExpand ? 'Collapse writeoffs' : 'Expand writeoffs'}
    >
      <div className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-light">Writeoffs</div>
      <span className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light cursor-pointer">
        {isExpand ? <Icon name="HiChevronDown" /> : <Icon name="HiChevronRight" />}
      </span>
    </button>
  );

  return (
    <Card>
      <CardHeader className={`${!isExpand ? '!border-none !mb-0 !pb-0' : ''}`}>
        {renderHeader()}
      </CardHeader>

      {isExpand && (
        <CardBody>
          <DataTable
            columns={columns}
            data={{
              data: data || [],
              total: data?.length || 0,
            }}
            emptyState={{
              title: 'No writeoffs',
              icon: 'FaDatabase',
              description: 'No writeoffs data',
            }}
            isLoading={isLoading}
            error={error}
            tableContainerClassName="overflow-y-auto relative"
            tableClassName="min-w-full divide-y divide-gray-200 relative"
            stickyHeader={true}
            loadingRowCount={5}
            loadingColumnCount={7}
          />
        </CardBody>
      )}
    </Card>
  );
};
